import React, { useEffect, useState, useCallback } from 'react';
import { Empty, notification, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Dynamics } from '@/services/dynamics/dynamics';
import { EntityItem } from '@/components/EntityItem/EntityItem';
import InputSearch from '@/components/InputSearch/InputSearch';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { EChannelProvider } from '@/services/solgari-api/enums/channel-provider.enum';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { DynamicsV2 } from '@/services/dynamics/dynamics-v2';
import { saveChatNoteEntity } from '@/shared/helpers/save-chat-note.helper';
import { observer } from 'mobx-react-lite';
import { IChatEntity } from '@/shared/interfaces/chat-entity.interface';
import { useLocation, useNavigate } from 'react-router-dom';
import { State } from '@/services/state.service';
import { useStore } from '@/stores';
import { commonMakeCall } from '@/shared/helpers/common-make-call.helper';
import { SipJs } from '@/services/sip-js.service';
import { CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import './Entities.scss';

export const Entities = observer(() => {
  const {
    chatsStore: {
      inboxChats,
      addChat,
      updateEntities,
      setGlobalSpinner,
      addChatLocally,
    },
    callStore: { callPage },
  } = useStore();

  const { page } = callPage;
  const [notificationApi, contextHolder] = notification.useNotification();
  notification.config({ maxCount: 1 });

  const navigate = useNavigate();
  const location = useLocation();
  const [listView, setListView] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOpeningEntity, setOpeningEntity] = useState(false);

  const antIcon = <LoadingOutlined spin />;

  const navigateToExistedChat = async (chat: IChatEntity, source: string) => {
    const chatNote = chat.chatNote;

    if (Dynamics.getVersion() === 2) {
      await DynamicsV2.createChatSession(
        chat.chatId,
        chatNote?.id,
        {
          source,
          identity: chat.externalCustomerIdentity,
          user: chat.user,
        },
        () =>
          navigate(`/inbox/${chat.chatId}`, {
            state: { skipOpening: true, from: location?.pathname },
          }),
      );
    } else {
      navigate(`/inbox/${chat.chatId}`, {
        state: { skipOpening: true, from: location?.pathname },
      });
    }

    setGlobalSpinner(false);
  };

  const createSms = async (phone: string, user: IEntity) => {
    try {
      setGlobalSpinner(true);

      const userid = user.id;
      const localChat = inboxChats.find(
        (c) => c.externalCustomerIdentity === phone,
      );

      if (localChat) {
        navigateToExistedChat(localChat, EChannelProvider.SMS);
        return;
      }

      const { chats } = await SolgariApiService.searchChatsByIdentity(
        EChannelProvider.SMS,
        userid,
      );
      const filteredChats = chats.filter((c) => c.state !== 'Stopped');

      if (filteredChats?.length) {
        const chat = filteredChats[0];
        const chatNote = (
          await Dynamics.searchChatNotes({ id: localChat.chatId })
        )?.[0];

        const chatEntity: IChatEntity = { ...chat, chatNote, user };
        addChatLocally(chatEntity);

        navigateToExistedChat(chatEntity, EChannelProvider.SMS);
        return;
      }

      // TODO: need to add e164 validation for phone number!!!
      // INFO: Was added for phone but should be reworked to e164
      const newChatId = await SolgariApiService.createNewChat(
        phone,
        EChannelProvider.SMS,
      );
      const id = await saveChatNoteEntity(
        newChatId,
        phone,
        EChannelProvider.SMS,
        user,
      );
      const chatNote = (await Dynamics.searchChatNotes({ id: newChatId }))?.[0];

      await addChat({
        chatId: newChatId,
        user,
        chatNote,
      });

      if (Dynamics.getVersion() === 2) {
        await DynamicsV2.createChatSession(
          newChatId,
          id,
          {
            source: 'SMS',
            identity: phone,
            user,
          },
          () =>
            navigate(`/inbox/${newChatId}`, {
              state: { skipOpening: true, from: location?.pathname },
            }),
        );
      } else {
        navigate(`/inbox/${newChatId}`, {
          state: { skipOpening: true, from: location?.pathname },
        });
      }

      setGlobalSpinner(false);
    } catch {
      setGlobalSpinner(false);
    }
  };

  const mapHistory = (user: IEntity, i: number) => {
    // TODO: check case if chat exists but closed,
    // I haven't changed a lot from v1 BUT NEED TO rework it by another way
    // need to just send a request which check exist the chat or not, if 'yes' -> navigateToChat, if 'no' create and navigate!!!

    return (
      <>
        <EntityItem
          key={`${user.id}_${i}`}
          user={user}
          onMakeCall={makeCall}
          onOpenEntity={openEntity}
          onCreateMail={createMail}
          onCreateSms={createSms}
        />
      </>
    );
  };

  const makeCall = async (phone: string, user: IEntity) => {
    const { state } = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    if (state !== 'granted') {
      notificationApi.error({
        message: 'Microphone permission is required',
        placement: 'bottomRight',
        duration: 5,
        className: 'notifications',
      });

      return;
    }
    if (SipJs.sessions?.length) {
      return;
    }

    const { isDialerActive } = State.state$.getValue();

    if (isDialerActive) {
      return;
    }

    Dynamics.searchEntityByCalls({ id: user.id }).then((entities) => {
      const updatedEntities: IEntity[] = [
        { ...entities[0], initialPhone: phone },
      ];

      commonMakeCall(updatedEntities, phone);
    });
  };

  const createMail = async (email: string, user: IEntity) => {
    try {
      setGlobalSpinner(true);
      // clear an email
      const emailLower = email.trim().toLowerCase();
      // trying to find locally
      const localChat = inboxChats.find(
        (c) => c.externalCustomerIdentity === email,
      );

      if (localChat) {
        navigateToExistedChat(localChat, EChannelProvider.EMAIL);
        return;
      }

      // trying to find remotely
      const { chats } = await SolgariApiService.searchChatsByIdentity(
        EChannelProvider.EMAIL,
        emailLower,
      );

      const filteredChats = chats.filter((c) => c.state !== 'Stopped');
      if (filteredChats.length) {
        const firstChat = filteredChats[0];
        const chatNote = (
          await Dynamics.searchChatNotes({ id: firstChat.chatId })
        )?.[0];
        const chatEntity: IChatEntity = { ...firstChat, chatNote, user };
        addChatLocally(chatEntity);

        navigateToExistedChat(chatEntity, EChannelProvider.EMAIL);
        return;
      }

      const newChatId = await SolgariApiService.createNewChat(
        email,
        EChannelProvider.OUTLOOK,
      );

      const id = await saveChatNoteEntity(
        newChatId,
        email,
        EChannelProvider.EMAIL,
        user,
      );
      const chatNote = (await Dynamics.searchChatNotes({ id: newChatId }))?.[0];

      await addChat({
        chatId: newChatId,
        user,
        chatNote,
      });

      if (Dynamics.getVersion() === 2) {
        await DynamicsV2.createChatSession(
          newChatId,
          id,
          {
            source: EChannelProvider.EMAIL,
            identity: email,
            user,
          },
          () =>
            navigate(`/inbox/${newChatId}`, {
              state: { skipOpening: true, from: location?.pathname },
            }),
        );
      } else {
        navigate(`/inbox/${newChatId}`, {
          state: { skipOpening: true, from: location?.pathname },
        });
      }

      setGlobalSpinner(false);
    } catch {
      setGlobalSpinner(false);
    }
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue?.(e.currentTarget.value);
  }, []);

  const openEntity = async (id: string, type: EEntity) => {
    if (isOpeningEntity) {
      return;
    }
    setOpeningEntity(true);
    await Dynamics.openEntity(id, type);
    setOpeningEntity(false);
  };

  const getEntitiesActivities = async () => {
    setIsLoaded(false);

    const entities = await Dynamics.searchEntities(searchValue, false, true);
    updateEntities(entities);

    const jsxListView = entities?.length ? (
      entities.map(mapHistory)
    ) : (
      <Empty className="empty" />
    );

    setListView(jsxListView);
    setIsLoaded(true);
  };

  useEffect(() => {
    if (!searchValue) {
      getEntitiesActivities();
      return;
    }

    const timerID = setTimeout(() => {
      getEntitiesActivities();
    }, 1000);

    return () => {
      clearTimeout(timerID);
    };
  }, [searchValue]);

  return (
    <div
      className={`entities-page ${page === CALL_PAGE_TYPE.MINIMIZED ? 'shortened' : ''}`}
    >
      {contextHolder}
      <InputSearch
        onChange={handleChange}
        data-testid="crm-entities-search-input"
        placeholder="Contact or Number"
        value={searchValue}
      />
      <div className="entities-content">
        {isLoaded ? listView : <Spin className="spinner" indicator={antIcon} />}
      </div>
    </div>
  );
});
